import React, { createContext, type ReactNode, useContext, useMemo } from "react";
import { type Release } from "../../../utils";
import GraphProvider from "../GraphProvider/GraphProvider";

export interface ReleaseContextValue {
  release: Release | null;
}

const ReleaseContext = createContext<ReleaseContextValue | null>(null);

export const useRelease = (): ReleaseContextValue | null => {
  const context = useContext(ReleaseContext);
  return context;
};

export interface ReleaseProviderProps {
  children: ReactNode;
  release: Release | null;
}

const ReleaseProvider = (props: ReleaseProviderProps) => {
  const { children, release } = props;

  const resolvedContext = useMemo(() => {
    return { release } satisfies ReleaseContextValue;
  }, [release]);

  return <ReleaseContext.Provider value={resolvedContext}>{children}</ReleaseContext.Provider>;
};

export interface ReleaseDependantProps {
  release?: Release;
}

export const useReleaseFromPropsOrContext = (
  props: ReleaseDependantProps,
): {
  release: Release | undefined;
  withReleaseProvider: (children: ReactNode) => ReactNode;
} => {
  const { release } = props;
  const context = useRelease();
  const resolvedRelease = release || context?.release;

  return {
    release: resolvedRelease || undefined,
    withReleaseProvider: (children: ReactNode) => {
      if (release) {
        return <ReleaseProvider release={release}>{children}</ReleaseProvider>;
      }
      return children;
    },
  };
};

export default ReleaseProvider;
